import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Box, Typography, Paper, Grid, CircularProgress, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Tab, TablePagination, Avatar, Collapse, IconButton, Drawer } from '@mui/material';
import { Edit as EditIcon, Email as EmailIcon, Phone as PhoneIcon, Sms as SmsIcon, WhatsApp as WhatsAppIcon, TrendingUp as TrendingUpIcon, Person as PersonIcon, CalendarMonth as CalendarIcon, Refresh as RefreshIcon, CheckCircle as CheckCircleIcon, Error as ErrorIcon, Pause as PauseIcon, HourglassEmpty as HourglassIcon, PlayArrow as PlayIcon, KeyboardArrowDown as ExpandIcon, KeyboardArrowUp as CollapseIcon, SmartToy as AIIcon, Timeline as TimelineIcon, LinkedIn as LinkedInIcon, FlagCircle as FlagIcon, Close as CloseIcon } from '@mui/icons-material';

import Chart from 'react-apexcharts';
import './index.css';
import TitleRoute from '../../components/titleRoute.js';
import { getWorkspacePath } from '../../utils/workspaceUtils';
import { getSingleCampaign, getCampaignAnalytics, getCampaignContacts, getActivityFeed } from '../../store/Campaign';

const StatCard = ({ title, value, subtitle, icon: Icon, color = 'primary', trend }) => (
  <Paper sx={{ p: 2.5, height: '100%' }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <Box>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>{title}</Typography>
        <Typography variant="h4" fontWeight={600}>{value}</Typography>
        {subtitle && <Typography variant="caption" color="textSecondary">{subtitle}</Typography>}
        {trend && <Chip label={`${trend > 0 ? '+' : ''}${trend}%`} size="small" color={trend >= 0 ? 'success' : 'error'} sx={{ mt: 1 }} />}
      </Box>
      {Icon && <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: `${color}.50`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon sx={{ color: `${color}.main` }} /></Box>}
    </Box>
  </Paper>
)

const channelConfig = {
  email: { icon: EmailIcon, color: '#1976d2', label: 'Email', sentKey: 'emailsSent', replyKey: 'emailsReplied', extraKeys: { opened: 'emailsOpened', clicked: 'emailsClicked', bounced: 'emailsBounced' } },
  sms: { icon: SmsIcon, color: '#0288d1', label: 'SMS', sentKey: 'smsSent', replyKey: 'smsReplied' },
  voice: { icon: PhoneIcon, color: '#2e7d32', label: 'Voice', sentKey: 'callsMade', replyKey: 'callsAnswered', extraKeys: { voicemail: 'callsVoicemail' } },
  whatsapp: { icon: WhatsAppIcon, color: '#25d366', label: 'WhatsApp', sentKey: 'whatsappSent', replyKey: 'whatsappReplied' },
  linkedin: { icon: LinkedInIcon, color: '#0077b5', label: 'LinkedIn', sentKey: 'linkedinSent', replyKey: 'linkedinReplied' }
};

const statusConfig = {
  pending: { color: '#6B7280', bg: '#F3F4F6', icon: HourglassIcon, label: 'Pending' },
  active: { color: '#2563EB', bg: '#EFF6FF', icon: PlayIcon, label: 'Active' },
  completed: { color: '#16A34A', bg: '#F0FDF4', icon: CheckCircleIcon, label: 'Completed' },
  failed: { color: '#DC2626', bg: '#FEF2F2', icon: ErrorIcon, label: 'Failed' },
  paused: { color: '#D97706', bg: '#FFFBEB', icon: PauseIcon, label: 'Paused' },
  opted_out: { color: '#6B7280', bg: '#F3F4F6', icon: PersonIcon, label: 'Opted Out' }
}

const execStatusConfig = {
  processing: { color: '#2563EB', bg: '#EFF6FF', label: 'Processing' },
  success: { color: '#16A34A', bg: '#F0FDF4', label: 'Success' },
  failed: { color: '#DC2626', bg: '#FEF2F2', label: 'Failed' },
  skipped: { color: '#6B7280', bg: '#F3F4F6', label: 'Skipped' }
}

const buildOrderedNodeList = (nodes, edges, entryNodeId) => {
  if (!nodes || nodes.length === 0) return [];
  const nodeMap = {};
  nodes.forEach(n => { nodeMap[n['id']] = n; });
  const edgeMap = {};
  (edges || []).forEach(e => { edgeMap[e['source']] = e['target']; });
  const ordered = [];
  const visited = new Set();
  let currentId = entryNodeId || nodes[0]?.['id'];
  while (currentId && nodeMap[currentId] && !visited.has(currentId)) {
    visited.add(currentId);
    ordered.push(nodeMap[currentId]);
    currentId = edgeMap[currentId];
  }
  if (ordered.length < nodes.length) {
    const remaining = nodes.filter(n => !visited.has(n['id'])).sort((a, b) => (a['position'] || 0) - (b['position'] || 0));
    ordered.push(...remaining);
  }
  return ordered;
};

const nodeTypeIcons = { email: EmailIcon, voice: PhoneIcon, sms: SmsIcon, whatsapp: WhatsAppIcon, linkedin: LinkedInIcon, delay: HourglassIcon, end: FlagIcon, condition: TimelineIcon };
const nodeTypeColors = { email: '#1976d2', voice: '#2e7d32', sms: '#0288d1', whatsapp: '#25d366', linkedin: '#0077b5', delay: '#D97706', end: '#16A34A', condition: '#7C3AED' };

const getTimelineNodeStyle = (timelineStatus, nodeType) => {
  const color = nodeTypeColors[nodeType] || '#6B7280';
  switch (timelineStatus) {
    case 'completed': return { borderColor: '#16A34A', bgColor: '#F0FDF4', iconColor: '#16A34A', lineColor: '#16A34A' };
    case 'failed': return { borderColor: '#DC2626', bgColor: '#FEF2F2', iconColor: '#DC2626', lineColor: '#DC2626' };
    case 'skipped': return { borderColor: '#6B7280', bgColor: '#F3F4F6', iconColor: '#6B7280', lineColor: '#6B7280' };
    case 'current-active':
    case 'current-waiting': return { borderColor: color, bgColor: `${color}15`, iconColor: color, lineColor: '#E5E7EB' };
    case 'upcoming':
    default: return { borderColor: '#D1D5DB', bgColor: '#F9FAFB', iconColor: '#D1D5DB', lineColor: '#E5E7EB' };
  }
};

const CountdownTimer = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState('');
  useEffect(() => {
    if (!targetDate) return;
    const calculate = () => {
      const diff = new Date(targetDate).getTime() - Date.now();
      if (diff <= 0) { setTimeLeft('Processing soon...'); return; }
      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      const parts = [];
      if (days > 0) parts.push(`${days}d`);
      if (hours > 0) parts.push(`${hours}h`);
      if (minutes > 0) parts.push(`${minutes}m`);
      parts.push(`${seconds}s`);
      setTimeLeft(parts.join(' '));
    };
    calculate();
    const interval = setInterval(calculate, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);
  if (!targetDate) return null;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.5, px: 1, py: 0.25, bgcolor: '#FFFBEB', borderRadius: 1, border: '1px solid #FDE68A' }}>
      <HourglassIcon sx={{ fontSize: 14, color: '#D97706', animation: 'pulse 2s infinite' }} />
      <Typography variant="caption" sx={{ fontWeight: 600, color: '#D97706', fontFamily: 'monospace', fontSize: 12 }}>{timeLeft}</Typography>
    </Box>
  );
};

const ContactExecutionHistory = ({ history, sequenceNodes, sequenceEdges, entryNodeId, currentNodeId, nextProcessAt, contactStatus, enrolledAt, completedAt }) => {
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const toggleExpand = useCallback((nodeId) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId); else next.add(nodeId);
      return next;
    });
  }, []);

  const timelineItems = useMemo(() => {
    const orderedNodes = buildOrderedNodeList(sequenceNodes, sequenceEdges, entryNodeId);
    if (orderedNodes.length === 0) return [];
    const historyMap = {};
    (history || []).forEach(entry => {
      if (!historyMap[entry['nodeId']]) historyMap[entry['nodeId']] = [];
      historyMap[entry['nodeId']].push(entry);
    });
    const isWaitingOnDelay = nextProcessAt && new Date(nextProcessAt) > new Date() && contactStatus === 'active';

    return orderedNodes.map((node, index) => {
      const historyEntries = historyMap[node['id']] || [];
      const latestEntry = historyEntries.length > 0 ? historyEntries[historyEntries.length - 1] : null;
      let timelineStatus;

      // After the engine processes a delay node, it marks it 'success' and advances currentNodeId
      // to the next node. But if nextProcessAt is still in the future, the delay is still active.
      const isDelayStillWaiting = node['type'] === 'delay' && latestEntry?.['status'] === 'success' && isWaitingOnDelay && node['id'] !== currentNodeId &&
        !historyMap[currentNodeId]?.length;

      if (isDelayStillWaiting) {
        timelineStatus = 'current-waiting';
      } else if (latestEntry) {
        if (latestEntry['status'] === 'success') timelineStatus = 'completed';
        else if (latestEntry['status'] === 'failed') timelineStatus = 'failed';
        else if (latestEntry['status'] === 'skipped') timelineStatus = 'skipped';
        else if (latestEntry['status'] === 'processing') timelineStatus = 'current-active';
        else timelineStatus = 'completed';
      } else if (node['id'] === currentNodeId) {
        // If currentNodeId points here but we're actually waiting on a previous delay, show as upcoming
        timelineStatus = isWaitingOnDelay && !historyMap[node['id']]?.length ? 'upcoming' : 'current-waiting';
      } else {
        timelineStatus = 'upcoming';
      }

      const showCountdown = isDelayStillWaiting || (node['id'] === currentNodeId && node['type'] === 'delay' && isWaitingOnDelay);
      return { node, index, historyEntry: latestEntry, timelineStatus, showCountdown, isLast: index === orderedNodes.length - 1 };
    });
  }, [sequenceNodes, sequenceEdges, entryNodeId, history, currentNodeId, nextProcessAt, contactStatus]);

  if ((!sequenceNodes || sequenceNodes.length === 0) && (!history || history.length === 0)) {
    return <Box sx={{ p: 3, textAlign: 'center' }}><Typography variant="body2" color="textSecondary">No execution timeline available</Typography></Box>;
  }

  return (
    <Box className="contact-timeline" sx={{ p: 2.5, bgcolor: '#FAFAFA' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><TimelineIcon sx={{ fontSize: 18 }} /> Execution Timeline</Typography>
        {enrolledAt && <Typography variant="caption" color="textSecondary">Enrolled {new Date(enrolledAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</Typography>}
      </Box>

      <Box className="timeline-container">
        {timelineItems.map((item) => {
          const { node, historyEntry, timelineStatus, showCountdown, isLast } = item;
          const style = getTimelineNodeStyle(timelineStatus, node['type']);
          const NodeIcon = nodeTypeIcons[node['type']] || TimelineIcon;
          const isExpanded = expandedNodes.has(node['id']);
          const isCurrent = timelineStatus === 'current-active' || timelineStatus === 'current-waiting';
          const isUpcoming = timelineStatus === 'upcoming';
          const duration = historyEntry?.['startedAt'] && historyEntry?.['completedAt'] ? Math.round((new Date(historyEntry['completedAt']) - new Date(historyEntry['startedAt'])) / 1000) : null;
          const timeStr = historyEntry?.['completedAt'] || historyEntry?.['startedAt'] ? new Date(historyEntry['completedAt'] || historyEntry['startedAt']).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : null;

          return (
            <Box key={node['id']} className={`timeline-item ${isCurrent ? 'timeline-item--current' : ''}`}>
              <Box className="timeline-item__track">
                <Box className={`timeline-item__dot ${isCurrent ? 'timeline-item__dot--pulse' : ''}`} sx={{ borderColor: style.borderColor, bgcolor: style.bgColor }}>
                  <NodeIcon sx={{ fontSize: 16, color: style.iconColor }} />
                </Box>
                {!isLast && <Box className="timeline-item__line" sx={{ bgcolor: style.lineColor }} />}
              </Box>

              <Box className="timeline-item__content" sx={{ opacity: isUpcoming ? 0.5 : 1, cursor: historyEntry ? 'pointer' : 'default' }} onClick={() => historyEntry && toggleExpand(node['id'])}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography variant="body2" fontWeight={600} sx={{ color: isUpcoming ? '#9CA3AF' : '#111827' }}>{node['name'] || 'Unnamed Step'}</Typography>
                    <Chip label={node['type']} size="small" variant="outlined" sx={{ fontSize: 10, height: 18, textTransform: 'capitalize', borderColor: isUpcoming ? '#D1D5DB' : style.borderColor, color: isUpcoming ? '#9CA3AF' : style.iconColor }} />
                    {historyEntry && <Chip label={(execStatusConfig[historyEntry['status']] || {})['label'] || historyEntry['status']} size="small" sx={{ bgcolor: (execStatusConfig[historyEntry['status']] || {})['bg'] || '#F3F4F6', color: (execStatusConfig[historyEntry['status']] || {})['color'] || '#6B7280', fontWeight: 500, fontSize: 10, height: 18 }} />}
                    {isCurrent && !historyEntry && <Chip label="Current" size="small" sx={{ bgcolor: '#EFF6FF', color: '#2563EB', fontWeight: 600, fontSize: 10, height: 18 }} />}
                    {isUpcoming && <Chip label="Upcoming" size="small" sx={{ bgcolor: '#F3F4F6', color: '#9CA3AF', fontSize: 10, height: 18 }} />}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {duration !== null && <Typography variant="caption" color="textSecondary">{duration}s</Typography>}
                    {timeStr && <Typography variant="caption" color="textSecondary">{timeStr}</Typography>}
                    {historyEntry && <IconButton size="small" sx={{ p: 0 }}>{isExpanded ? <CollapseIcon sx={{ fontSize: 16 }} /> : <ExpandIcon sx={{ fontSize: 16 }} />}</IconButton>}
                  </Box>
                </Box>

                {node['type'] === 'delay' && (
                  <Box sx={{ mt: 0.5 }}>
                    {showCountdown && <Box sx={{ mt: 0.5 }}><CountdownTimer targetDate={nextProcessAt} /></Box>}
                  </Box>
                )}

                {historyEntry && (historyEntry['isAIMode'] || historyEntry['confidenceScore'] > 0) && (
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                    {historyEntry['isAIMode'] && <Chip icon={<AIIcon sx={{ fontSize: '12px !important' }} />} label="AI Generated" size="small" sx={{ fontSize: 10, height: 20, bgcolor: '#F3E8FF', color: '#7C3AED' }} />}
                    {historyEntry['confidenceScore'] > 0 && <Chip label={`${Math.round(historyEntry['confidenceScore'])}% confidence`} size="small" sx={{ fontSize: 10, height: 20, bgcolor: historyEntry['confidenceScore'] >= 70 ? '#F0FDF4' : '#FFFBEB', color: historyEntry['confidenceScore'] >= 70 ? '#16A34A' : '#D97706' }} />}
                  </Box>
                )}

                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box sx={{ mt: 1.5 }}>
                    {historyEntry?.['emailPreview'] && (historyEntry['emailPreview']['subject'] || historyEntry['emailPreview']['body']) && (
                      <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid #E5E7EB', mb: 1 }}>
                        {historyEntry['emailPreview']['subject'] && <Typography variant="body2" fontWeight={500} sx={{ fontSize: 12 }}>Subject: {historyEntry['emailPreview']['subject']}</Typography>}
                        {historyEntry['emailPreview']['body'] && <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, fontSize: 11, maxHeight: 120, overflow: 'auto' }} dangerouslySetInnerHTML={{ __html: historyEntry['emailPreview']['body'] }} />}
                      </Box>
                    )}
                    {historyEntry?.['error'] && (
                      <Box sx={{ p: 1, bgcolor: '#FEF2F2', borderRadius: 1, border: '1px solid #FECACA', mb: 1 }}>
                        <Typography variant="caption" sx={{ color: '#DC2626' }}>{historyEntry['error']}{historyEntry['retryCount'] > 0 && ` (Retried ${historyEntry['retryCount']}x)`}</Typography>
                      </Box>
                    )}
                    {historyEntry?.['tokensUsed'] && historyEntry['tokensUsed']['total'] > 0 && (
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: 11 }}>Tokens: {historyEntry['tokensUsed']['total']} (P: {historyEntry['tokensUsed']['prompt']} / C: {historyEntry['tokensUsed']['completion']})</Typography>
                    )}
                    {historyEntry?.['startedAt'] && (
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, fontSize: 11 }}>
                        Started: {new Date(historyEntry['startedAt']).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        {historyEntry['completedAt'] && ` | Completed: ${new Date(historyEntry['completedAt']).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })}`}
                      </Typography>
                    )}
                  </Box>
                </Collapse>

                {isUpcoming && node['type'] === 'email' && node['config']?.['subject'] && (
                  <Typography variant="caption" sx={{ color: '#9CA3AF', mt: 0.5, display: 'block' }}>Will send: "{node['config']['subject']}"</Typography>
                )}
              </Box>
            </Box>
          );
        })}

        {contactStatus === 'completed' && completedAt && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, pl: '14px' }}>
            <CheckCircleIcon sx={{ fontSize: 16, color: '#16A34A' }} />
            <Typography variant="caption" sx={{ color: '#16A34A', fontWeight: 500 }}>Sequence completed {new Date(completedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

const CampaignAnalytics = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const routerHistory = useHistory();
  const contactsFetchKeyRef = useRef('');
  const activityFetchedRef = useRef(false);
  const [activeTab, setActiveTab] = useState(0);
  const [overviewSubTab, setOverviewSubTab] = useState(0);
  const [contactPage, setContactPage] = useState(0);
  const [contactLimit, setContactLimit] = useState(20);
  const [contactFilter, setContactFilter] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);

  const { currentWorkspace } = useSelector(state => state['Settings']);
  const { singleCampaign, singleCampaignLoading, analytics, campaignContacts, campaignContactsLoading, campaignContactsTotal, activityFeed, activityFeedLoading } = useSelector(state => state['Campaign']);

  useEffect(() => {
    if (id) {
      dispatch(getSingleCampaign(id));
      dispatch(getCampaignAnalytics(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (id && activeTab === 1) {
      const key = `${id}-${contactPage}-${contactLimit}-${contactFilter}`;
      if (contactsFetchKeyRef.current === key) return;
      contactsFetchKeyRef.current = key;
      dispatch(getCampaignContacts({ id, page: contactPage + 1, limit: contactLimit, status: contactFilter || undefined }));
    }
  }, [dispatch, id, activeTab, contactPage, contactLimit, contactFilter]);

  // Lazy load activity feed when Activity sub-tab is first clicked
  useEffect(() => {
    if (id && overviewSubTab === 1 && !activityFetchedRef.current) {
      activityFetchedRef.current = true;
      dispatch(getActivityFeed({ id, page: 1, limit: 50 }));
    }
  }, [dispatch, id, overviewSubTab]);

  const handleRefresh = () => {
    dispatch(getCampaignAnalytics(id));
    if (activeTab === 1) {
      contactsFetchKeyRef.current = '';
      dispatch(getCampaignContacts({ id, page: contactPage + 1, limit: contactLimit, status: contactFilter || undefined }));
    }
    if (overviewSubTab === 1) {
      activityFetchedRef.current = false;
      dispatch(getActivityFeed({ id, page: 1, limit: 50 }));
    }
  };

  // Compute multi-channel stats from analytics.metrics
  const metrics = singleCampaign?.['metrics'] || {};
  const contactStats = analytics?.['contactStats'] || {};

  const totalSent = (metrics['emailsSent'] || 0) + (metrics['smsSent'] || 0) + (metrics['callsMade'] || 0) + (metrics['whatsappSent'] || 0) + (metrics['linkedinSent'] || 0);
  const totalReplies = (metrics['emailsReplied'] || 0) + (metrics['smsReplied'] || 0) + (metrics['callsAnswered'] || 0) + (metrics['whatsappReplied'] || 0) + (metrics['linkedinReplied'] || 0);
  const activeChannelCount = Object.values(channelConfig).filter(ch => (metrics[ch['sentKey']] || 0) > 0).length;
  const responseRate = (metrics['totalContacts'] || 0) > 0 ? Math.round((totalReplies / metrics['totalContacts']) * 100) : 0;

  // Build ordered step analytics from backend data
  const orderedStepAnalytics = useMemo(() => {
    if (!analytics?.['stepAnalytics'] || !singleCampaign?.['sequence']) return analytics?.['stepAnalytics'] || [];
    const orderedNodes = buildOrderedNodeList(singleCampaign['sequence']['nodes'] || [], singleCampaign['sequence']['edges'] || [], singleCampaign['sequence']['entryNodeId']);
    const stepMap = {};
    (analytics['stepAnalytics'] || []).forEach(s => { stepMap[s['_id']] = s; });
    return orderedNodes.map(node => stepMap[node['id']] || { _id: node['id'], nodeName: node['name'], nodeType: node['type'], sent: 0, failed: 0, skipped: 0, totalTokens: 0, aiCount: 0 });
  }, [analytics, singleCampaign]);

  // Build time-series chart data from analytics.timelineChart
  const chartData = useMemo(() => {
    const timeline = analytics?.['timelineChart'] || [];
    if (timeline.length === 0) return { series: [], categories: [] };
    const dateSet = new Set();
    const channelData = {};
    timeline.forEach(entry => {
      const date = entry['_id']?.['date'];
      const channel = entry['_id']?.['channel'];
      if (!date || !channel) return;
      dateSet.add(date);
      if (!channelData[channel]) channelData[channel] = {};
      channelData[channel][date] = (channelData[channel][date] || 0) + (entry['sent'] || 0);
    });
    const categories = Array.from(dateSet).sort();
    const series = Object.entries(channelData).map(([channel, data]) => ({
      name: channelConfig[channel]?.['label'] || channel,
      data: categories.map(date => data[date] || 0),
      color: channelConfig[channel]?.['color'] || '#6B7280'
    }));
    return { series, categories };
  }, [analytics]);

  const areaChartOptions = useMemo(() => ({
    chart: { type: 'area', toolbar: { show: false }, zoom: { enabled: false } },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 2 },
    xaxis: { categories: chartData['categories'], labels: { style: { fontSize: '11px' }, formatter: (val) => { if (!val) return ''; const d = new Date(val); return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); } } },
    yaxis: { labels: { style: { fontSize: '11px' } } },
    fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.1, stops: [0, 100] } },
    tooltip: { x: { format: 'MMM dd, yyyy' } },
    legend: { position: 'top', horizontalAlign: 'right', fontSize: '12px' },
    colors: chartData['series'].map(s => s['color']),
    grid: { borderColor: '#F3F4F6' }
  }), [chartData]);

  if (singleCampaignLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}><CircularProgress /></Box>;

  const campaign = singleCampaign || {};

  return (
    <div className="page-content campaign-page-content campaign-wizard">
      <div className="campaign-wizard-header">
        <div className="wizard-header-left">
          <button className="header-exit-btn" onClick={() => routerHistory.push(getWorkspacePath('/campaigns', currentWorkspace?.['_id']))}>
            Exit
          </button>
          <h1 className="campaign-title">{campaign['name']}</h1>
          {campaign['status'] && <Chip label={campaign['status']} size="small" color={campaign['status'] === 'active' ? 'success' : campaign['status'] === 'paused' ? 'warning' : 'default'} sx={{ ml: 1 }} />}
        </div>

        <div className="wizard-header-right">
          <IconButton onClick={handleRefresh} size="small"><RefreshIcon /></IconButton>
          <button className="btn-save-continue" onClick={() => routerHistory.push(getWorkspacePath(`/campaigns/${id}`, currentWorkspace?.['_id']))}>
            <EditIcon fontSize="small" /> Edit
          </button>
        </div>
      </div>

      <div className="campaign-wizard-content">
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight={600} sx={{ mb: 1.5 }}>Campaign Analytics</Typography>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: 13 } }}>
            <Tab label="Overview" />
            <Tab label="Contacts" />
          </Tabs>
        </Box>
        {activeTab === 0 && (
          <>
            {/* Row 1: 5 Multi-Channel Stat Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={4} md={2.4}><StatCard title="Contacts Enrolled" value={metrics['totalContacts'] || 0} subtitle={`${metrics['contactsReached'] || 0} reached`} icon={PersonIcon} color="primary" /></Grid>
              <Grid item xs={6} sm={4} md={2.4}><StatCard title="Messages Sent" value={totalSent} subtitle={activeChannelCount > 0 ? `across ${activeChannelCount} channel${activeChannelCount !== 1 ? 's' : ''}` : undefined} icon={EmailIcon} color="info" /></Grid>
              <Grid item xs={6} sm={4} md={2.4}><StatCard title="Replies Received" value={totalReplies} subtitle={totalSent > 0 ? `${Math.round((totalReplies / totalSent) * 100)}% reply rate` : undefined} icon={TrendingUpIcon} color="success" /></Grid>
              <Grid item xs={6} sm={4} md={2.4}><StatCard title="Response Rate" value={`${responseRate}%`} subtitle="of contacts replied" icon={TrendingUpIcon} color="warning" /></Grid>
              <Grid item xs={6} sm={4} md={2.4}><StatCard title="Meetings Booked" value={metrics['meetingsBooked'] || 0} subtitle={(metrics['totalContacts'] || 0) > 0 ? `${Math.round(((metrics['meetingsBooked'] || 0) / metrics['totalContacts']) * 100)}% conversion` : undefined} icon={CalendarIcon} color="secondary" /></Grid>
            </Grid>

            {/* Row 2: Multi-Channel Area Chart */}
            {chartData['series'].length > 0 && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Messages Over Time</Typography>
                <Chart options={areaChartOptions} series={chartData['series']} type="area" height={280} />
              </Paper>
            )}

            {/* Row 3: Channel Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {Object.entries(channelConfig).filter(([, ch]) => (metrics[ch['sentKey']] || 0) > 0).map(([key, ch]) => {
                const sent = metrics[ch['sentKey']] || 0;
                const replied = metrics[ch['replyKey']] || 0;
                const replyRate = sent > 0 ? Math.round((replied / sent) * 100) : 0;
                const ChIcon = ch['icon'];
                return (
                  <Grid item xs={6} sm={4} md key={key}>
                    <Paper sx={{ p: 2, borderTop: `3px solid ${ch['color']}` }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <ChIcon sx={{ color: ch['color'], fontSize: 20 }} />
                        <Typography variant="subtitle2" fontWeight={600}>{ch['label']}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="textSecondary">{key === 'voice' ? 'Made' : 'Sent'}</Typography>
                          <Typography variant="body2" fontWeight={500}>{sent}</Typography>
                        </Box>
                        {ch['extraKeys']?.['opened'] && <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="textSecondary">Opened</Typography>
                          <Typography variant="body2" fontWeight={500}>{metrics[ch['extraKeys']['opened']] || 0}{sent > 0 && <Typography component="span" variant="caption" color="textSecondary"> | {Math.round(((metrics[ch['extraKeys']['opened']] || 0) / sent) * 100)}%</Typography>}</Typography>
                        </Box>}
                        {ch['extraKeys']?.['clicked'] && <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="textSecondary">Clicked</Typography>
                          <Typography variant="body2" fontWeight={500}>{metrics[ch['extraKeys']['clicked']] || 0}{sent > 0 && <Typography component="span" variant="caption" color="textSecondary"> | {Math.round(((metrics[ch['extraKeys']['clicked']] || 0) / sent) * 100)}%</Typography>}</Typography>
                        </Box>}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="textSecondary">{key === 'voice' ? 'Answered' : 'Replied'}</Typography>
                          <Typography variant="body2" fontWeight={500}>{replied}<Typography component="span" variant="caption" color="textSecondary"> | {replyRate}%</Typography></Typography>
                        </Box>
                        {ch['extraKeys']?.['bounced'] && <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="textSecondary">Bounced</Typography>
                          <Typography variant="body2" fontWeight={500} sx={{ color: (metrics[ch['extraKeys']['bounced']] || 0) > 0 ? '#DC2626' : undefined }}>{metrics[ch['extraKeys']['bounced']] || 0}</Typography>
                        </Box>}
                        {ch['extraKeys']?.['voicemail'] && <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="textSecondary">Voicemail</Typography>
                          <Typography variant="body2" fontWeight={500}>{metrics[ch['extraKeys']['voicemail']] || 0}</Typography>
                        </Box>}
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>

            {/* Row 4: Step Analytics / Activity sub-tabs */}
            <Paper sx={{ overflow: 'hidden' }}>
              <Box sx={{ borderBottom: '1px solid #E5E7EB' }}>
                <Tabs value={overviewSubTab} onChange={(e, v) => setOverviewSubTab(v)} sx={{ minHeight: 36, px: 2, '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: 13 } }}>
                  <Tab label="Step Analytics" />
                  <Tab label="Activity" />
                </Tabs>
              </Box>

              {overviewSubTab === 0 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Step</TableCell>
                        <TableCell>Channel</TableCell>
                        <TableCell align="right">Sent</TableCell>
                        <TableCell align="right">Failed</TableCell>
                        <TableCell align="right">Skipped</TableCell>
                        <TableCell align="right">AI Generated</TableCell>
                        <TableCell align="right">Tokens</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orderedStepAnalytics.length === 0 ? (
                        <TableRow><TableCell colSpan={7} align="center"><Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>No step data yet</Typography></TableCell></TableRow>
                      ) : orderedStepAnalytics.map((step) => {
                        const StepIcon = nodeTypeIcons[step['nodeType']] || TimelineIcon;
                        const color = nodeTypeColors[step['nodeType']] || '#6B7280';
                        const isDelay = step['nodeType'] === 'delay' || step['nodeType'] === 'end';
                        return (
                          <TableRow key={step['_id']}>
                            <TableCell><Typography variant="body2" fontWeight={500}>{step['nodeName'] || 'Unnamed'}</Typography></TableCell>
                            <TableCell><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><StepIcon sx={{ fontSize: 16, color }} /><Typography variant="body2" sx={{ textTransform: 'capitalize', color }}>{step['nodeType']}</Typography></Box></TableCell>
                            <TableCell align="right">{isDelay ? '—' : step['sent'] || 0}</TableCell>
                            <TableCell align="right" sx={{ color: (step['failed'] || 0) > 0 ? '#DC2626' : undefined }}>{isDelay ? '—' : step['failed'] || 0}</TableCell>
                            <TableCell align="right">{isDelay ? '—' : step['skipped'] || 0}</TableCell>
                            <TableCell align="right">{isDelay ? '—' : (step['aiCount'] || 0) > 0 ? <Chip icon={<AIIcon sx={{ fontSize: '12px !important' }} />} label={`${step['aiCount']} (${step['sent'] > 0 ? Math.round((step['aiCount'] / step['sent']) * 100) : 0}%)`} size="small" sx={{ fontSize: 10, height: 20, bgcolor: '#F3E8FF', color: '#7C3AED' }} /> : '0'}</TableCell>
                            <TableCell align="right">{(step['totalTokens'] || 0) > 0 ? step['totalTokens'].toLocaleString() : '—'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {overviewSubTab === 1 && (
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {activityFeedLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={24} /></Box>
                  ) : (activityFeed || []).length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}><Typography variant="body2" color="textSecondary">No activity yet</Typography></Box>
                  ) : (activityFeed || []).map((activity, idx) => {
                    const AIcon = nodeTypeIcons[activity['nodeType']] || TimelineIcon;
                    const aColor = nodeTypeColors[activity['nodeType']] || '#6B7280';
                    const statusLabel = activity['status'] === 'success' ? 'Sent' : activity['status'] === 'failed' ? 'Failed' : activity['status'];
                    const statusColor = activity['status'] === 'failed' ? '#DC2626' : '#16A34A';
                    return (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 1.5, borderBottom: '1px solid #F3F4F6', '&:hover': { bgcolor: '#FAFAFA' } }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: activity['status'] === 'failed' ? '#FEF2F2' : '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <AIcon sx={{ fontSize: 16, color: activity['status'] === 'failed' ? '#DC2626' : aColor }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Typography variant="body2" fontWeight={500} sx={{ color: statusColor }}>{statusLabel}</Typography>
                            {activity['isAIMode'] && <Chip label="AI" size="small" sx={{ fontSize: 9, height: 16, bgcolor: '#F3E8FF', color: '#7C3AED' }} />}
                          </Box>
                          <Typography variant="caption" color="textSecondary" noWrap>{activity['contactEmail'] || activity['contactName']?.trim() || 'Unknown'}</Typography>
                        </Box>
                        <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>{activity['completedAt'] ? new Date(activity['completedAt']).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ minWidth: 60, textAlign: 'right' }}>{activity['nodeName'] || ''}</Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Paper>
          </>
        )}

        {activeTab === 1 && (
          <>
            {/* Stats chips */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
              {[{ key: '', label: 'All', count: contactStats ? Object.values(contactStats).reduce((a, b) => a + b, 0) : campaignContactsTotal }, ...(contactStats ? Object.entries(statusConfig).filter(([k]) => contactStats[k]).map(([k, v]) => ({ key: k, label: v['label'], count: contactStats[k] || 0 })) : [])].map(item => (
                <Chip key={item['key']} label={`${item['label']} (${item['count']})`} variant={contactFilter === item['key'] ? 'filled' : 'outlined'} color={contactFilter === item['key'] ? 'primary' : 'default'} onClick={() => { setContactFilter(item['key']); setContactPage(0); }} sx={{ fontWeight: 500 }} />
              ))}
            </Box>

            <Paper sx={{ overflow: 'hidden' }}>
              {campaignContactsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={32} /></Box>
              ) : (campaignContacts || []).length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <PersonIcon sx={{ fontSize: 48, color: '#D1D5DB', mb: 1 }} />
                  <Typography variant="body1" color="textSecondary">No contacts enrolled yet</Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>Contacts will appear here once the campaign is launched</Typography>
                </Box>
              ) : (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Contact</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Current Step</TableCell>
                          <TableCell align="center">Emails Sent</TableCell>
                          <TableCell>Last Activity</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(campaignContacts || []).map(cc => {
                          const contact = cc['contactId'] || {};
                          const name = [contact['firstName'], contact['lastName']].filter(Boolean).join(' ') || contact['email'] || 'Unknown';
                          const initials = (contact['firstName']?.[0] || '') + (contact['lastName']?.[0] || '') || '?';
                          const cfg = statusConfig[cc['status']] || statusConfig['pending'];
                          const emailsSent = (cc['executionHistory'] || []).filter(h => h['nodeType'] === 'email' && h['status'] === 'success').length;
                          const lastEntry = (cc['executionHistory'] || []).slice(-1)[0];
                          const lastActivity = lastEntry?.['completedAt'] ? new Date(lastEntry['completedAt']).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : cc['enrolledAt'] ? new Date(cc['enrolledAt']).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
                          const currentNode = (campaign['sequence']?.['nodes'] || []).find(n => n['id'] === cc['currentNodeId']);

                          return (
                            <TableRow key={cc['_id']} hover onClick={() => setSelectedContact(cc)} sx={{ cursor: 'pointer', bgcolor: selectedContact?.['_id'] === cc['_id'] ? '#F0F7FF' : undefined }}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Avatar sx={{ width: 32, height: 32, fontSize: 13, bgcolor: '#E5E7EB', color: '#374151' }}>{initials}</Avatar>
                                  <Box>
                                    <Typography variant="body2" fontWeight={500}>{name}</Typography>
                                    {contact['email'] && name !== contact['email'] && <Typography variant="caption" color="textSecondary">{contact['email']}</Typography>}
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip label={cfg['label']} size="small" sx={{ bgcolor: cfg['bg'], color: cfg['color'], fontWeight: 500, fontSize: 12 }} icon={<cfg.icon sx={{ fontSize: '14px !important', color: `${cfg['color']} !important` }} />} />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="textSecondary">{currentNode?.['name'] || (cc['status'] === 'completed' ? 'Completed' : '-')}</Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body2">{emailsSent}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" color="textSecondary">{lastActivity}</Typography>
                                {cc['lastError'] && <Typography variant="caption" sx={{ display: 'block', color: '#DC2626', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cc['lastError']}</Typography>}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination component="div" count={campaignContactsTotal} page={contactPage} onPageChange={(e, p) => setContactPage(p)} rowsPerPage={contactLimit} onRowsPerPageChange={(e) => { setContactLimit(parseInt(e['target']['value'], 10)); setContactPage(0); }} rowsPerPageOptions={[10, 20, 50]} />
                </>
              )}
            </Paper>

          </>
        )}
      </div>

      {/* Contact Timeline Drawer */}
      <Drawer anchor="right" open={!!selectedContact} onClose={() => setSelectedContact(null)} PaperProps={{ sx: { width: { xs: '100vw', sm: 480 } } }}>
        {selectedContact && (() => {
          const cc = selectedContact;
          const contact = cc['contactId'] || {};
          const name = [contact['firstName'], contact['lastName']].filter(Boolean).join(' ') || contact['email'] || 'Unknown';
          const initials = (contact['firstName']?.[0] || '') + (contact['lastName']?.[0] || '') || '?';
          const cfg = statusConfig[cc['status']] || statusConfig['pending'];
          const emailsSent = (cc['executionHistory'] || []).filter(h => h['nodeType'] === 'email' && h['status'] === 'success').length;
          const totalTokens = cc['tokenUsage']?.['totalTokens'] || 0;

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {/* Drawer Header */}
              <Box sx={{ p: 2.5, borderBottom: '1px solid #E5E7EB' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600}>Contact Timeline</Typography>
                  <IconButton size="small" onClick={() => setSelectedContact(null)}><CloseIcon fontSize="small" /></IconButton>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar sx={{ width: 48, height: 48, fontSize: 16, bgcolor: '#E5E7EB', color: '#374151' }}>{initials}</Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>{name}</Typography>
                    {contact['email'] && <Typography variant="body2" color="textSecondary">{contact['email']}</Typography>}
                    {contact['company'] && <Typography variant="caption" color="textSecondary">{contact['company']}</Typography>}
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label={cfg['label']} size="small" sx={{ bgcolor: cfg['bg'], color: cfg['color'], fontWeight: 500, fontSize: 12 }} icon={<cfg.icon sx={{ fontSize: '14px !important', color: `${cfg['color']} !important` }} />} />
                  <Chip label={`${emailsSent} email${emailsSent !== 1 ? 's' : ''} sent`} size="small" variant="outlined" sx={{ fontSize: 11 }} icon={<EmailIcon sx={{ fontSize: '14px !important' }} />} />
                  {totalTokens > 0 && <Chip label={`${totalTokens} tokens`} size="small" variant="outlined" sx={{ fontSize: 11 }} icon={<AIIcon sx={{ fontSize: '14px !important' }} />} />}
                  {cc['errorCount'] > 0 && <Chip label={`${cc['errorCount']} error${cc['errorCount'] !== 1 ? 's' : ''}`} size="small" sx={{ bgcolor: '#FEF2F2', color: '#DC2626', fontSize: 11 }} icon={<ErrorIcon sx={{ fontSize: '14px !important', color: '#DC2626 !important' }} />} />}
                </Box>
              </Box>

              {/* Timeline Content */}
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                <ContactExecutionHistory
                  history={cc['executionHistory'] || []}
                  sequenceNodes={campaign['sequence']?.['nodes'] || []}
                  sequenceEdges={campaign['sequence']?.['edges'] || []}
                  entryNodeId={campaign['sequence']?.['entryNodeId']}
                  currentNodeId={cc['currentNodeId']}
                  nextProcessAt={cc['nextProcessAt']}
                  contactStatus={cc['status']}
                  enrolledAt={cc['enrolledAt']}
                  completedAt={cc['completedAt']}
                />
              </Box>
            </Box>
          );
        })()}
      </Drawer>
    </div>
  )
}

export default CampaignAnalytics;